import { __extends, __assign, __rest } from 'tslib';
import React__default, { createRef } from 'react';
import clsx from 'clsx';
import { IconX } from '@tabler/icons';
import ButtonBase from '../ButtonBase/ButtonBase.component.js';
import colors from '../../util/colors.js';
import { Variants, propTypes, defaultProps } from './Input.types.js';
import styles from './Input.module.scss.js';

var Input = /** @class */ (function (_super) {
    __extends(Input, _super);
    function Input(props) {
        var _this = _super.call(this, props) || this;
        _this.setInputRef = function (instance) {
            var inputRef = _this.props.inputRef;
            _this.inputElement.current = instance;
            if (inputRef) {
                if (typeof inputRef === 'function') {
                    inputRef(instance);
                }
                else {
                    inputRef.current = instance;
                }
            }
        };
        _this.handleClearInput = function (event) {
            event.preventDefault();
            event.stopPropagation();
            var _a = _this.props, onChange = _a.onChange, name = _a.name;
            var inputElement = _this.inputElement.current;
            var changeEvent = __assign(__assign({}, event), { currentTarget: inputElement, target: inputElement });
            // in some case typescript think that name can be 'undefined'
            // even though it's set in default props as 'input'
            // so 'if' statement need to avoid this error
            if (name) {
                changeEvent.target.name = name;
            }
            changeEvent.target.value = '';
            onChange && onChange(changeEvent);
        };
        _this.getClassName = function (componentName) {
            var _a = _this.props, rounded = _a.rounded, value = _a.value, isValid = _a.isValid, alignRight = _a.alignRight, clearable = _a.clearable, icon = _a.icon, variant = _a.variant, disabled = _a.disabled;
            var hasValue = !!value && value !== 0;
            var isValidInput = disabled || isValid !== false;
            var isBasic = !disabled && isValidInput && variant !== Variants.UNDERLINE;
            var selectedVariant = rounded ? Variants.ROUNDED : _this.props.variant;
            return clsx(styles[componentName], styles[componentName + "___" + selectedVariant], alignRight && styles[componentName + "___alignRight"], icon && styles[componentName + "___withIcon"], hasValue && clearable && styles[componentName + "___notEmpty"], !isValidInput && styles[componentName + "___invalid"], isBasic && styles[componentName + "___basic"], disabled && styles[componentName + "___disabled"]);
        };
        // createRef() returns a readonly "current" property, which later has it's value re-assigned
        // this doesn't cause any runtime problems, which is why the ts issue is ignored
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        _this.inputElement = createRef();
        return _this;
    }
    Input.prototype.componentDidMount = function () {
        if (this.props.testId) {
            console.warn('DEPRECATION WARNING: The `testId` prop is deprecated and support for it will be removed soon. Please use `data-testid` instead.');
        }
        if (this.props.rounded) {
            console.warn('DEPRECATION WARNING: The `rounded` prop is deprecated and support for it will be removed soon. Please use `variant` instead.');
        }
        if (this.props.errorMessage) {
            console.warn('DEPRECATION WARNING: The `errorMessage` is deprecated and will be removed in our Next major release. Please use secondaryCondition + isValid instead');
        }
        if (this.props.relativelyStyledConditions) {
            console.warn('DEPRECATION WARNING: Use isMultilineSecondaryCondition instead. `relativelyStyledConditions` is scheduled to be removed in the next major release');
        }
    };
    Input.prototype.render = function () {
        var _a = this.props, rounded = _a.rounded, label = _a.label, name = _a.name, isValid = _a.isValid, errorMessage = _a.errorMessage, className = _a.className, primaryConditionClassName = _a.primaryConditionClassName, secondaryConditionClassName = _a.secondaryConditionClassName; 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _a.alignRight; var clearable = _a.clearable, icon = _a.icon, primaryCondition = _a.primaryCondition, secondaryCondition = _a.secondaryCondition, variant = _a.variant, testId = _a.testId, hideLabel = _a.hideLabel, relativelyStyledConditions = _a.relativelyStyledConditions, isSecondaryConditionMultiline = _a.isMultilineSecondaryCondition; 
        // inputProps is destructured on input tag, and inputRef is not supported attribute
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _a.inputRef; var id = _a.id, inputProps = __rest(_a, ["rounded", "label", "name", "isValid", "errorMessage", "className", "primaryConditionClassName", "secondaryConditionClassName", "alignRight", "clearable", "icon", "primaryCondition", "secondaryCondition", "variant", "testId", "hideLabel", "relativelyStyledConditions", "isMultilineSecondaryCondition", "inputRef", "id"]);
        var selectedVariant = rounded ? Variants.ROUNDED : this.props.variant;
        var shouldShowPrimaryCondition = primaryCondition && selectedVariant !== 'underline';
        var isMultilineSecondaryCondition = isSecondaryConditionMultiline || relativelyStyledConditions;
        return (React__default.createElement("div", { className: clsx(this.getClassName('Input'), className), "data-testid": "parent-" + testId },
            React__default.createElement("div", { className: clsx(styles.Input_titleWrapper, hideLabel && styles.Input_titleWrapper___hidden) },
                React__default.createElement("label", { className: clsx(styles.InputLabel, !label && styles.InputLabel___hidden), htmlFor: id || name }, label),
                shouldShowPrimaryCondition && (React__default.createElement("div", { className: clsx(styles.InputCondition, primaryConditionClassName, styles.InputCondition___primary) }, primaryCondition))),
            React__default.createElement("div", { className: styles.Input_elementWrapper },
                React__default.createElement("input", __assign({ ref: this.setInputRef, autoComplete: "off", name: name, className: this.getClassName('InputElement'), "data-variant": variant, "data-testid": testId, id: id, "aria-describedby": "error-message" }, inputProps)),
                icon && (React__default.createElement("div", { className: this.getClassName('InputIcon') }, icon)),
                clearable && (React__default.createElement(ButtonBase, { onClick: this.handleClearInput, type: "button", tabIndex: -1, "data-testid": 'close-button', className: styles.InputCloseButton, title: "Close", style: {
                        width: '2.75rem',
                        height: '2.75rem',
                        border: 'none',
                        backgroundColor: 'transparent',
                    } },
                    React__default.createElement("svg", { width: "18", height: "18", viewBox: "0 0 24 24", style: {
                            position: 'absolute',
                            left: 'calc(50% - 9px)',
                            top: 'calc(50% - 8.5px)',
                        } },
                        React__default.createElement("circle", { fill: "#BBC0CE", cx: "12", cy: "12", r: "9" })),
                    React__default.createElement(IconX, { color: colors.white, size: 8 })))),
            (secondaryCondition ||
                !isValid ||
                isMultilineSecondaryCondition) && (React__default.createElement("div", { role: "alert", id: "error-message", className: clsx(styles.InputCondition, secondaryConditionClassName, isMultilineSecondaryCondition
                    ? styles.InputCondition___relativeSecondary
                    : styles.InputCondition___secondary), "data-testid": "secondary-condition" }, secondaryCondition || errorMessage))));
    };
    Input.displayName = 'Input';
    Input.propTypes = propTypes;
    Input.defaultProps = __assign({}, defaultProps);
    return Input;
}(React__default.Component));

export { Input as default };
